// İki adımlı doğrulama arayüz parçaları (madde 12). Uygulamadan bağımsız:
// HTTP çağrıları, QR çizimi ve telefon alanı uygulamadan verilir; metinler
// `strings`, düğme sınıfları `buttons` ile uygulamaya uydurulur. Yerleşim
// kendi CSS'iyle gelir (twoFactor.css) — pencere, sayfa ya da kart içinde
// aynı görünür.

import { FormEvent, ReactNode, useState } from "react";
import "./twoFactor.css";

export type TwoFactorMethod = "TOTP" | "SMS";

export interface TwoFactorStrings {
  codeTitle: string;
  codeHintTotp: string;
  codeHintSms: (phoneHint: string | null) => string;
  codeHintBackup: string;
  codeLabel: string;
  verify: string;
  resend: string;
  back: string;
  setupTitle: string;
  setupIntro: string;
  methodTotp: string;
  methodSms: string;
  start: string;
  totpScan: string;
  totpManual: string;
  smsPhoneLabel: string;
  smsSent: (phoneHint: string | null) => string;
  enable: string;
  backupTitle: string;
  backupHint: string;
  copy: string;
  copied: string;
  done: string;
  /** Oturumdan kurulumda şifre onayı alanı (`requirePassword`). */
  passwordLabel?: string;
}

export const DEFAULT_TWO_FACTOR_STRINGS: TwoFactorStrings = {
  codeTitle: "İki Adımlı Doğrulama",
  codeHintTotp: "Doğrulama uygulamanızdaki (Google/Microsoft Authenticator vb.) 6 haneli kodu girin.",
  codeHintSms: (p) => `${p ? `${p} numaralı telefona` : "Telefonunuza"} gönderilen 6 haneli kodu girin.`,
  codeHintBackup: "Telefonunuza erişemiyorsanız yedek kodlarınızdan birini (xxxx-xxxx-xxxx-xxxx) girebilirsiniz.",
  codeLabel: "Doğrulama kodu",
  verify: "Doğrula",
  resend: "Kodu yeniden gönder",
  back: "Geri",
  setupTitle: "İki Adımlı Doğrulamayı Kur",
  setupIntro: "Girişte şifrenize ek olarak bir doğrulama kodu istenecek.",
  methodTotp: "Doğrulama uygulaması (önerilir)",
  methodSms: "SMS ile kod",
  start: "Devam",
  totpScan: "Doğrulama uygulamanızla bu QR kodu okutun, sonra uygulamadaki 6 haneli kodu girin.",
  totpManual: "QR okutamıyorsanız bu anahtarı elle girin:",
  smsPhoneLabel: "Cep telefonu",
  smsSent: (p) => `${p ?? "Telefonunuza"} doğrulama kodu gönderildi.`,
  enable: "Etkinleştir",
  backupTitle: "Yedek Kodlar",
  backupHint:
    "Telefonunuzu kaybederseniz bu kodlarla giriş yapabilirsiniz. Her kod bir kez kullanılır. Güvenli bir yere kaydedin — bir daha gösterilmeyecek.",
  copy: "Kopyala",
  copied: "Kopyalandı",
  done: "Kaydettim, devam et",
  passwordLabel: "Şifreniz (onay için)",
};

/** Uygulamanın düğme sınıfları (varsayılan: btn-primary / btn-secondary). */
export interface TwoFactorButtonClasses {
  primary?: string;
  secondary?: string;
}

const DEFAULT_BUTTONS: Required<TwoFactorButtonClasses> = { primary: "btn-primary", secondary: "btn-secondary" };

// ---------- Girişte kod adımı ----------

export interface TwoFactorCodeFormProps {
  method: TwoFactorMethod | null;
  phoneHint?: string | null;
  busy?: boolean;
  onSubmit: (code: string) => void;
  onResend?: () => void;
  onBack?: () => void;
  strings?: TwoFactorStrings;
  buttons?: TwoFactorButtonClasses;
}

export function TwoFactorCodeForm({
  method,
  phoneHint = null,
  busy,
  onSubmit,
  onResend,
  onBack,
  strings = DEFAULT_TWO_FACTOR_STRINGS,
  buttons,
}: TwoFactorCodeFormProps) {
  const btn = { ...DEFAULT_BUTTONS, ...buttons };
  const [code, setCode] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.trim()) onSubmit(code.trim());
  };
  return (
    <form onSubmit={submit} className="tfa-form">
      <h3>{strings.codeTitle}</h3>
      <p className="tfa-hint">{method === "SMS" ? strings.codeHintSms(phoneHint) : strings.codeHintTotp}</p>
      <label className="tfa-field">
        {strings.codeLabel}
        <input
          className="tfa-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="text"
          autoComplete="one-time-code"
          autoFocus
          maxLength={20}
          required
        />
      </label>
      <p className="tfa-hint tfa-hint--backup">{strings.codeHintBackup}</p>
      <button type="submit" className={btn.primary} disabled={busy}>
        {strings.verify}
      </button>
      {(onBack || (method === "SMS" && onResend)) && (
        <div className="tfa-actions tfa-actions--split">
          {onBack ? (
            <button type="button" className={btn.secondary} onClick={onBack}>
              {strings.back}
            </button>
          ) : (
            <span />
          )}
          {method === "SMS" && onResend && (
            <button type="button" className={btn.secondary} onClick={onResend} disabled={busy}>
              {strings.resend}
            </button>
          )}
        </div>
      )}
    </form>
  );
}

// ---------- Kurulum sihirbazı ----------

export type TwoFactorSetupStart =
  | { method: "TOTP"; secret: string; otpauthUrl: string }
  | { method: "SMS"; phoneHint: string | null };

export interface TwoFactorSetupProps {
  smsAvailable: boolean;
  /** Sunucuya kurulum başlat (POST /2fa/setup). Hata fırlatırsa `onError`.
   *  `password`: `requirePassword` açıksa kullanıcının girdiği şifre (gövdeye eklenmeli). */
  start: (method: TwoFactorMethod, phone?: string, password?: string) => Promise<TwoFactorSetupStart>;
  /** Oturum açmış kullanıcının kendi kurulumunda (profil) şifre onayı iste.
   *  Girişteki zorunlu kurulumda verilmez (şifre az önce girildi). */
  requirePassword?: boolean;
  /** Kodu onayla (POST /2fa/enable) → yedek kodlar. */
  enable: (code: string) => Promise<string[]>;
  /** Yedek kodlar kaydedildikten sonra. */
  onDone: () => void;
  onError: (err: unknown) => void;
  /** İlk adımda "Vazgeç"/"Geri" düğmesi (isteğe bağlı). */
  onCancel?: () => void;
  cancelLabel?: string;
  renderQr: (otpauthUrl: string) => ReactNode;
  renderPhoneInput: (value: string, onChange: (v: string) => void) => ReactNode;
  strings?: TwoFactorStrings;
  buttons?: TwoFactorButtonClasses;
}

export function TwoFactorSetup({
  smsAvailable,
  start,
  enable,
  onDone,
  onError,
  onCancel,
  cancelLabel,
  renderQr,
  renderPhoneInput,
  requirePassword = false,
  strings = DEFAULT_TWO_FACTOR_STRINGS,
  buttons,
}: TwoFactorSetupProps) {
  const btn = { ...DEFAULT_BUTTONS, ...buttons };
  const [method, setMethod] = useState<TwoFactorMethod>("TOTP");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [started, setStarted] = useState<TwoFactorSetupStart | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  if (backupCodes) return <BackupCodesView codes={backupCodes} onDone={onDone} strings={strings} buttons={buttons} />;

  if (!started) {
    return (
      <form
        className="tfa-form"
        onSubmit={(e) => {
          e.preventDefault();
          run(async () =>
            setStarted(await start(method, method === "SMS" ? phone : undefined, requirePassword ? password : undefined)),
          );
        }}
      >
        <h3>{strings.setupTitle}</h3>
        <p className="tfa-hint">{strings.setupIntro}</p>
        {smsAvailable ? (
          <div className="tfa-methods" role="radiogroup">
            {(
              [
                ["TOTP", strings.methodTotp],
                ["SMS", strings.methodSms],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className={`tfa-method${method === value ? " tfa-method--active" : ""}`}>
                <input type="radio" name="tfa-method" checked={method === value} onChange={() => setMethod(value)} />
                {label}
              </label>
            ))}
          </div>
        ) : (
          <p>
            <strong>{strings.methodTotp}</strong>
          </p>
        )}
        {method === "SMS" && (
          <label className="tfa-field">
            {strings.smsPhoneLabel}
            {renderPhoneInput(phone, setPhone)}
          </label>
        )}
        {requirePassword && (
          <label className="tfa-field">
            {strings.passwordLabel ?? DEFAULT_TWO_FACTOR_STRINGS.passwordLabel}
            <input
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
        )}
        <div className="tfa-actions">
          {onCancel && (
            <button type="button" className={btn.secondary} onClick={onCancel}>
              {cancelLabel ?? strings.back}
            </button>
          )}
          <button
            type="submit"
            className={btn.primary}
            disabled={busy || (method === "SMS" && !phone) || (requirePassword && !password)}
          >
            {strings.start}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form
      className="tfa-form"
      onSubmit={(e) => {
        e.preventDefault();
        run(async () => setBackupCodes(await enable(code.trim())));
      }}
    >
      <h3>{strings.setupTitle}</h3>
      {started.method === "TOTP" ? (
        <>
          <p className="tfa-hint">{strings.totpScan}</p>
          <div className="tfa-qr">{renderQr(started.otpauthUrl)}</div>
          <p className="tfa-hint">
            {strings.totpManual} <code className="tfa-secret">{started.secret}</code>
          </p>
        </>
      ) : (
        <p className="tfa-hint">{strings.smsSent(started.phoneHint)}</p>
      )}
      <label className="tfa-field">
        {strings.codeLabel}
        <input
          className="tfa-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          autoFocus
          required
        />
      </label>
      <div className="tfa-actions">
        <button type="button" className={btn.secondary} onClick={() => setStarted(null)}>
          {strings.back}
        </button>
        <button type="submit" className={btn.primary} disabled={busy}>
          {strings.enable}
        </button>
      </div>
    </form>
  );
}

// ---------- Yedek kodlar ----------

export function BackupCodesView({
  codes,
  onDone,
  strings = DEFAULT_TWO_FACTOR_STRINGS,
  buttons,
}: {
  codes: string[];
  onDone: () => void;
  strings?: TwoFactorStrings;
  buttons?: TwoFactorButtonClasses;
}) {
  const btn = { ...DEFAULT_BUTTONS, ...buttons };
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
    } catch {
      /* pano erişimi yoksa kullanıcı elle kopyalar */
    }
  };
  return (
    <div className="tfa-form">
      <h3>{strings.backupTitle}</h3>
      <p className="tfa-hint">{strings.backupHint}</p>
      <ul className="tfa-codes">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="tfa-actions">
        <button type="button" className={btn.secondary} onClick={copy}>
          {copied ? strings.copied : strings.copy}
        </button>
        <button type="button" className={btn.primary} onClick={onDone}>
          {strings.done}
        </button>
      </div>
    </div>
  );
}
